import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from 'react-map-gl/maplibre';

import { useIsAdmin } from '../auth/useIsAdmin';
import { useAdminSession } from '../auth/useAdminSession';
import {
  PLATES_AVAILABLE, PLATES_MAX_MOUNTED, PLATES_MIN_ZOOM, PLATE_LAYERS, boundsIntersect, compareDraw,
  compareDrawIn, defaultOn, errBand, errOf, errTitle, featureBounds, filterActive, formatErr,
  inDateRange, movePlate, orderedByHand, plateFilter, plateKey, plateOpacity, plateThumbnailUrl,
  plateVisible, plateYear, resetPlateOrder, resetPlateVisibility, setPlateFilter, setPlatePrefs,
  usePlatesState, PLATES_NEED_SESSION,
} from './platesConfig';
import './PlatesPanel.css';

/** Rows rendered at once. Zoomed out, hundreds of plates can be in view. */
const ROW_CAP = 100;

/** [w, s, e, n] of the map view, and its zoom. */
const readView = (map) => {
  const b = map.getBounds();
  return { bounds: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()], zoom: map.getZoom() };
};

/**
 * List order: plates shown by default first, then the smallest error bound,
 * then the one drawn higher. Within the default-on plates of one layer that is
 * the map's own stacking for A-number plates (ordered by error bound).
 */
const listOrder = (layer) => (a, b) => (
  (Number(defaultOn(b.properties)) - Number(defaultOn(a.properties)))
  || ((errOf(a.properties) ?? Infinity) - (errOf(b.properties) ?? Infinity))
  || compareDraw(layer, b.properties, layer, a.properties)
);

/**
 * The plate list, on the left of the map: every plate whose footprint touches
 * the current view, with a picture of it, where it came from, its error bound,
 * and its own show/hide and opacity. One tab per plate layer when more than
 * one is on. At most ROW_CAP rows are rendered; a chip narrows the list to
 * the plates shown by default.
 */
export function PlatesPanel({ overlayVisibility }) {
  const { current: map } = useMap();
  const plates = usePlatesState();
  const isAdmin = useIsAdmin();
  const [collapsed, setCollapsed] = useState(false);
  const [tab, setTab] = useState(null);
  const [view, setView] = useState(null);
  const [only, setOnly] = useState('all'); // 'all' | 'default'
  const listRef = useRef(null);

  // A restricted source (A-number, through the API) waits for the session.
  const session = useAdminSession(PLATES_NEED_SESSION);
  const layers = PLATES_AVAILABLE && isAdmin
    ? PLATE_LAYERS.filter((p) => overlayVisibility?.[p.key] && (!p.restricted || session))
    : [];
  const layerKeys = layers.map((l) => l.key).join(',');

  useEffect(() => {
    if (!map || !layerKeys) return undefined;
    const update = () => setView(readView(map));
    update();
    map.on('moveend', update);
    return () => { map.off('moveend', update); };
  }, [map, layerKeys]);

  const inView = useMemo(() => Object.fromEntries(layers.map((l) => [
    l.key,
    (plates.index[l.key]?.features || [])
      .filter((f) => boundsIntersect(featureBounds(f), view?.bounds)).sort(listOrder(l)),
  ])), [layerKeys, plates.index, view]); // eslint-disable-line react-hooks/exhaustive-deps

  // Every year present in each layer's whole index, for the date filter.
  const years = useMemo(() => Object.fromEntries(layers.map((l) => [l.key,
    [...new Set((plates.index[l.key]?.features || []).map((f) => plateYear(l, f.properties))
      .filter((y) => y != null))].sort((a, b) => a - b)])),
  [layerKeys, plates.index]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!layers.length) return null;

  // The tab picked, while its layer stays on; otherwise the first layer that
  // has something in view, so the panel never opens on an empty tab by default.
  const active = layers.find((l) => l.key === tab)
    || layers.find((l) => inView[l.key]?.length)
    || layers[0];

  // Date filter first: a plate outside the range is neither listed nor drawn.
  const filter = plateFilter(plates, active);
  const dated = (inView[active.key] || []).filter((f) => inDateRange(plates, active, f.properties));
  // On the map, top of the stack first - the order the user rearranges.
  const cmp = compareDrawIn(plates);
  const shown = dated.filter((f) => plateVisible(plates, active, f))
    .sort((a, b) => cmp(active, b.properties, active, a.properties));
  const stack = shown.map((f) => plateKey(active, f.properties));
  const hidden = dated.filter((f) => !plateVisible(plates, active, f));
  const hiddenListed = only === 'default' ? hidden.filter((f) => defaultOn(f.properties)) : hidden;
  const shownRows = shown.slice(0, ROW_CAP);
  const hiddenRows = hiddenListed.slice(0, ROW_CAP);
  const handOrdered = stack.some((k) => orderedByHand(plates, k));
  const loading = plates.index[active.key]?.status === 'loading';
  const keysOf = (fs) => fs.map((f) => plateKey(active, f.properties));
  const listed = [...shown, ...hiddenListed];
  const setAll = (visible) => setPlatePrefs(Object.fromEntries(keysOf(listed).map((k) => [k, { visible }])));

  // What the map can draw of this: nothing below the zoom gate, and at most
  // PLATES_MAX_MOUNTED of the plates shown here (PlatesOverlay keeps the best).
  const zoomedOut = view && view.zoom < PLATES_MIN_ZOOM;
  const shownHere = layers.reduce((n, l) => n
    + (inView[l.key] || []).filter((f) => plateVisible(plates, l, f) && inDateRange(plates, l, f.properties)).length, 0);

  return (
    <div className={`plates-panel${collapsed ? ' plates-panel--collapsed' : ''}`}>
      <div className="plates-panel__head">
        <span className="plates-panel__title">Plates in view</span>
        <button type="button" className="plates-panel__icon-btn"
                onClick={() => setCollapsed(!collapsed)}
                aria-expanded={!collapsed}
                title={collapsed ? 'Show the plate list' : 'Hide the plate list'}>
          {collapsed ? '▸' : '▾'}
        </button>
      </div>

      {!collapsed && (
        <>
          {layers.length > 1 && (
            <div className="plates-panel__tabs" role="tablist">
              {layers.map((l) => (
                <button key={l.key} type="button" role="tab"
                        aria-selected={l.key === active.key}
                        className={`plates-panel__tab${l.key === active.key ? ' is-active' : ''}`}
                        onClick={() => setTab(l.key)}>
                  {l.tab} <span className="plates-panel__count">{(inView[l.key] || []).length}</span>
                </button>
              ))}
            </div>
          )}

          <DateFilter layer={active} filter={filter} years={years[active.key] || []}
                      hiddenByDate={(inView[active.key] || []).length - dated.length} />

          {zoomedOut && (
            <div className="plates-panel__note">
              Zoom in to draw plates (zoom {PLATES_MIN_ZOOM}+). The list works at any zoom.
            </div>
          )}
          {!zoomedOut && shownHere > PLATES_MAX_MOUNTED && (
            <div className="plates-panel__note">
              Drawing {PLATES_MAX_MOUNTED} of about {shownHere} shown plates here: those stacked by
              hand first, then the best-placed. Zoom in to draw the rest.
            </div>
          )}

          {listed.length > 1 && (
            <div className="plates-panel__bulk">
              <button type="button" onClick={() => setAll(true)} title="Show every listed plate">Show all</button>
              <button type="button" onClick={() => setAll(false)} title="Hide every listed plate">Hide all</button>
              <button type="button" onClick={() => resetPlateVisibility(keysOf(listed))}
                      title="Forget show/hide choices for the listed plates: each goes back to its default">
                Defaults
              </button>
            </div>
          )}

          <div className="plates-panel__scroll" ref={listRef}>
            {/* On the map: top of the stack first, rearrangeable. */}
            <div className="plates-panel__section">
              <span>On the map <span className="plates-panel__count">{shown.length}</span></span>
              {handOrdered && (
                <button type="button" className="plates-panel__link"
                        onClick={() => resetPlateOrder(stack)}
                        title="Forget the hand stacking of these plates: back to the default order (best placement on top)">
                  Reset order
                </button>
              )}
            </div>
            {shown.length > 0 && (
              <div className="plates-panel__hint">Top of the list draws on top. Use ▲ ▼ to restack.</div>
            )}
            <ul className="plates-panel__list">
              {shownRows.map((f, i) => (
                <PlateRow key={plateKey(active, f.properties)} layer={active} feature={f}
                          plates={plates} map={map} listRef={listRef}
                          stack={stack} position={i} />
              ))}
            </ul>
            {shown.length > shownRows.length && (
              <div className="plates-panel__more">
                {shown.length - shownRows.length} more shown here, not listed. Zoom in to narrow the list.
              </div>
            )}
            {!shown.length && !loading && (
              <div className="plates-panel__empty">No plate here is on the map.</div>
            )}

            {/* Everything else in view: switched off, listed by default-on then error bound. */}
            <div className="plates-panel__section">
              <span>Not shown <span className="plates-panel__count">{hidden.length}</span></span>
              <span className="plates-panel__chips" role="group" aria-label="Which hidden plates to list">
                <button type="button" className={only === 'default' ? 'is-on' : ''}
                        aria-pressed={only === 'default'} onClick={() => setOnly('default')}
                        title="List only default-on plates among the hidden ones">
                  Default-on
                </button>
                <button type="button" className={only === 'all' ? 'is-on' : ''}
                        aria-pressed={only === 'all'} onClick={() => setOnly('all')}
                        title="List every hidden plate in view">
                  All
                </button>
              </span>
            </div>
            <ul className="plates-panel__list">
              {hiddenRows.map((f) => (
                <PlateRow key={plateKey(active, f.properties)} layer={active} feature={f}
                          plates={plates} map={map} listRef={listRef} />
              ))}
            </ul>
            {hiddenListed.length > hiddenRows.length && (
              <div className="plates-panel__more">
                {hiddenListed.length - hiddenRows.length} more plate{hiddenListed.length - hiddenRows.length === 1 ? '' : 's'} in view
                {' '}not listed. Zoom in to narrow the list.
              </div>
            )}
            {!hiddenListed.length && (
              <div className="plates-panel__empty">
                {loading ? 'Loading plates…'
                  : hidden.length ? 'No default-on plates among the hidden ones. Choose “All”.'
                    : dated.length ? 'Every plate here is on the map.'
                      : (inView[active.key] || []).length ? 'No plates here in the chosen years.'
                        : 'No plates in this part of the map.'}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Year range for the active layer - ASX by announcement date, A-number by
 * report year. Applies to the list and the map alike. `years` are the years
 * present in the layer's whole index, so the choices never offer an empty year.
 */
function DateFilter({ layer, filter, years, hiddenByDate }) {
  if (!years.length) return null;
  const set = (patch) => setPlateFilter(layer.key, patch);
  const num = (v) => (v === '' ? null : Number(v));
  const active = filterActive(filter);
  return (
    <div className="plates-panel__dates">
      <span className="plates-panel__dates-label">{layer.key === 'asxPlates' ? 'Announced' : 'Report year'}</span>
      <select value={filter.from ?? ''} onChange={(e) => set({ from: num(e.target.value) })}
              aria-label="From year">
        <option value="">any</option>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      <span>–</span>
      <select value={filter.to ?? ''} onChange={(e) => set({ to: num(e.target.value) })}
              aria-label="To year">
        <option value="">any</option>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      {active && (
        <>
          <label className="plates-panel__undated" title="Keep plates with no known date">
            <input type="checkbox" checked={filter.undated} onChange={() => set({ undated: !filter.undated })} />
            undated
          </label>
          <button type="button" className="plates-panel__link" onClick={() => set({ from: null, to: null })}>
            Clear
          </button>
        </>
      )}
      {active && hiddenByDate > 0 && (
        <span className="plates-panel__dates-note">{hiddenByDate} outside the range</span>
      )}
    </div>
  );
}

/** The plate's error bound, coloured by band. */
function ErrBadge({ props }) {
  const err = errOf(props);
  return (
    <span className={`plates-panel__err plates-panel__err--${errBand(err)}`} title={errTitle(props)}>
      {formatErr(err)}
    </span>
  );
}

/**
 * A thumbnail that asks GeoServer for its GetMap only once its row scrolls
 * near the list's visible area. `loading="lazy"` alone measures against the
 * page, not the scrolling list, and would let dozens of rows fire at once.
 */
function LazyThumb({ src, listRef }) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    if (seen || !ref.current) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setSeen(true); return undefined; }
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect(); }
    }, { root: listRef.current || null, rootMargin: '120px 0px' });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen, listRef]);

  return (
    <span ref={ref} className="plates-panel__thumb-slot">
      {seen && src && <img src={src} alt="" loading="lazy" decoding="async" />}
    </span>
  );
}

function PlateRow({ layer, feature, plates, map, listRef, stack, position }) {
  const key = plateKey(layer, feature.properties);
  const visible = plateVisible(plates, layer, feature);
  const opacity = plateOpacity(plates, key);
  const item = layer.listItem(feature.properties);
  const thumb = useMemo(() => plateThumbnailUrl(layer, feature), [layer, feature]);

  const zoomTo = () => {
    const b = featureBounds(feature);
    if (map && b) map.fitBounds([[b[0], b[1]], [b[2], b[3]]], { padding: 40, duration: 800 });
  };

  return (
    <li className={`plates-panel__row${visible ? '' : ' is-hidden'}`}>
      <button type="button" className="plates-panel__thumb" onClick={zoomTo}
              title="Zoom to this plate">
        <LazyThumb src={thumb} listRef={listRef} />
      </button>
      <div className="plates-panel__body">
        <div className="plates-panel__title-row">
          <button type="button" className="plates-panel__name" onClick={zoomTo} title="Zoom to this plate">
            {item.title}
          </button>
          <ErrBadge props={feature.properties} />
        </div>
        {stack && (
          <div className="plates-panel__stack" role="group" aria-label={`Stacking of ${item.title}`}>
            <button type="button" disabled={position === 0} onClick={() => movePlate(key, stack, 'top')}
                    title="Bring to the top of the stack">⤒</button>
            <button type="button" disabled={position === 0} onClick={() => movePlate(key, stack, 'up')}
                    title="Move up one: draws over the plate above it">▲</button>
            <button type="button" disabled={position === stack.length - 1} onClick={() => movePlate(key, stack, 'down')}
                    title="Move down one: draws under the plate below it">▼</button>
            <span className="plates-panel__stack-pos">{position + 1} of {stack.length}</span>
          </div>
        )}
        {item.source && <div className="plates-panel__source">{item.source}</div>}
        {item.sub && <div className="plates-panel__sub">{item.sub}</div>}
        <div className="plates-panel__controls">
          <label className="plates-panel__show">
            <input type="checkbox" checked={visible}
                   onChange={() => setPlatePrefs({ [key]: { visible: !visible } })} />
            Show
          </label>
          <input type="range" className="gf-range plates-panel__opacity"
                 min={0.1} max={1} step={0.05} value={opacity} disabled={!visible}
                 aria-label={`Opacity of ${item.title}`}
                 style={{ '--gf-range-fill': `${Math.round(((opacity - 0.1) / 0.9) * 100)}%` }}
                 onChange={(e) => setPlatePrefs({ [key]: { opacity: parseFloat(e.target.value) } })} />
          <span className="plates-panel__pct">{Math.round(opacity * 100)}%</span>
        </div>
      </div>
    </li>
  );
}
